/**
 * QuoteFormNew — kompakt tabel-baseret formular til den nye adresse-model.
 *
 * Layout pr. sektion:
 *   - Google-adressesøg som første felt (per linje i tabellen)
 *   - Tabel-rækker for hver afhentning/bortkørsel
 *   - Varer som indrykkede ekstra-rækker under hver bevægelseslinje
 *   - Hver vare → DORA cargo-invoice-line ved convert
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateQuote, useUpdateQuote } from '../../hooks/useApi';
import {
  Box, Button, Card, CardContent, CircularProgress, Grid, IconButton, MenuItem,
  TextField, Typography, Alert, Snackbar, Divider, Autocomplete,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Stack,
  FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '../../services/api';
import PlacesAutocomplete, { type PlaceResult } from '../../components/common/PlacesAutocomplete';
import type {
  Quote, QuoteAddress, QuoteMovementLine, QuoteMovementItem, QuoteType, QuoteCreateInput,
  QuotePriceLine, SalesLocation, QuoteVisibility,
} from '../../types';
import { DEFAULT_QUOTE_VISIBILITY } from '../../types';

interface DoraProduct {
  id: string;
  productNumber: string;
  name: string;
  unitId: string | null;
  salesPrice: string | null;
}

interface DoraFinanceUnit { id: string; name: string }

const CARGO_TYPES: Array<{ value: string; label: string }> = [
  { value: 'PALLET_GOODS', label: 'Pallegods' },
  { value: 'GENERAL_CARGO', label: 'Stykgods' },
  { value: 'COOL', label: 'Køl' },
  { value: 'FREEZE', label: 'Frost' },
  { value: 'BULK_CARGO', label: 'Bulk' },
  { value: 'CONTAINER', label: 'Container' },
  { value: 'MACHINES', label: 'Maskiner' },
  { value: 'PACKAGES', label: 'Pakker' },
  { value: 'WASTE', label: 'Affald' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'DELIVERY', label: 'Levering' },
  { value: 'COLLECTION', label: 'Afhentning' },
  { value: 'MISCELLANEOUS', label: 'Diverse' },
];

const QUOTE_TYPES: Array<{ value: QuoteType; label: string }> = [
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'CONTAINER', label: 'Container' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'WASTE', label: 'Affald/Bortkørsel' },
  { value: 'RECURRING', label: 'Tilbagevendende' },
];

const emptyAddress = (): QuoteAddress => ({
  salesLocationId: null, placeName: '', address: '', postcode: '', city: '', country: 'DK',
  contact: '', contactPhone: '', ref: '', date: '', time: '', items: [],
});

const emptyLine = (): QuoteMovementLine => ({
  id: crypto.randomUUID(),
  salesLocationId: null, placeName: '', address: '', postcode: '', city: '', country: 'DK',
  contact: '', contactPhone: '',
  date: '', time: '',
  cargoType: 'MISCELLANEOUS', description: '', weight: '', loadmeters: '',
  items: [],
});

const emptyItem = (): QuoteMovementItem => ({
  id: crypto.randomUUID(),
  productId: null, productNumber: '', productName: '', description: '',
  quantity: 1, unitName: 'stk', unitPrice: 0,
});

// Standardenheder (DORA's API har ikke et /units endpoint — units er fritekst pr. faktura-linje).
// Listen herunder er almindelige danske transport-enheder; brugeren kan også taste sin egen.
const STANDARD_UNITS = ['stk', 'kg', 'ton', 'm³', 'ldm', 'palle', 'kolli', 'time', 'dag', 'tur', 'km', 'pers'];

interface ExtraPriceLineForm { description: string; amount: number; quantity: number; unitName: string; }
const emptyExtra = (): ExtraPriceLineForm => ({ description: '', amount: 0, quantity: 1, unitName: 'stk' });

const fmtMoney = (n: number) => n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lineTotal = (l: QuoteMovementLine) =>
  (l.items || []).reduce((s, it) => s + (it.unitPrice || 0) * (it.quantity || 1), 0);

// Mindre, kompakt input — bruges i tabel-cellerne
const cellSx = { '& .MuiInputBase-input': { py: 0.5, px: 0.75, fontSize: '0.825rem' }, '& .MuiInputBase-root': { fontSize: '0.825rem' } };

export default function QuoteFormNew() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState(false);
  const createQuote = useCreateQuote();
  const updateQuote = useUpdateQuote();
  const [existingQuote, setExistingQuote] = useState<Quote | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Form state
  const [quoteType, setQuoteType] = useState<QuoteType>('TRANSPORT');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [quoteAddress, setQuoteAddress] = useState<QuoteAddress>(emptyAddress());
  const [pickupLines, setPickupLines] = useState<QuoteMovementLine[]>([]);
  const [dropoffLines, setDropoffLines] = useState<QuoteMovementLine[]>([]);
  const [extraPriceLines, setExtraPriceLines] = useState<ExtraPriceLineForm[]>([]);
  const [validUntil, setValidUntil] = useState('');
  const [terms, setTerms] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [visibility, setVisibility] = useState<QuoteVisibility>(DEFAULT_QUOTE_VISIBILITY);
  const [templateActiveFrom, setTemplateActiveFrom] = useState('');
  const [templateActiveUntil, setTemplateActiveUntil] = useState('');

  // Lookup data
  // DORA returnerer { client, clientNumber, clientEmail, clientPhone, clientAddress } —
  // vi normaliserer til vores interne form ved load.
  const [customers, setCustomers] = useState<Array<{ name: string; customerNumber: string; email: string | null; phone: string | null }>>([]);
  const [salesLocations, setSalesLocations] = useState<SalesLocation[]>([]);
  const [products, setProducts] = useState<DoraProduct[]>([]);
  const [units, setUnits] = useState<DoraFinanceUnit[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [cs, locs, prodData] = await Promise.all([
          api.getQuoteCustomers().catch(() => []),
          api.getSalesLocations().catch(() => []),
          api.getQuoteProducts().catch(() => ({ products: [], surcharges: [], units: [] })),
        ]);
        // Mapper DORA's felter til vores interne navne
        setCustomers((cs || []).map((c: any) => ({
          name: c.client || '',
          customerNumber: c.clientNumber || '',
          email: c.clientEmail || null,
          phone: c.clientPhone || null,
        })));
        setSalesLocations(locs || []);
        setProducts(prodData.products || []);
        setUnits((prodData as any).units || []);
      } catch { /* ignore */ }

      if (isEdit && id) {
        try {
          const q = await api.getQuote(id);
          setExistingQuote(q);
          setQuoteType(q.quoteType);
          setCustomerName(q.customerName);
          setCustomerEmail(q.customerEmail);
          setCustomerPhone(q.customerPhone || '');
          setCustomerNumber(q.customerNumber || '');
          setQuoteAddress({ ...emptyAddress(), ...(q.quoteAddress || {}) });
          setPickupLines((q.pickupLines || []).map(l => ({
            ...emptyLine(), ...l,
            id: l.id || crypto.randomUUID(),
            items: (l.items || []).map(i => ({ ...emptyItem(), ...i, id: i.id || crypto.randomUUID() })),
          })));
          setDropoffLines((q.dropoffLines || []).map(l => ({
            ...emptyLine(), ...l,
            id: l.id || crypto.randomUUID(),
            items: (l.items || []).map(i => ({ ...emptyItem(), ...i, id: i.id || crypto.randomUUID() })),
          })));
          setExtraPriceLines((q.priceLines || []).filter(pl => !pl.loadId).map(pl => ({
            description: pl.description, amount: pl.amount, quantity: pl.quantity, unitName: pl.unitName || 'stk',
          })));
          setValidUntil(q.validUntil ? q.validUntil.slice(0, 10) : '');
          setTerms(q.terms || '');
          setCustomerMessage(q.customerMessage || '');
          setInternalNote(q.internalNote || '');
          setVisibility({ ...DEFAULT_QUOTE_VISIBILITY, ...((q as any).quoteVisibility || {}) });
          setTemplateActiveFrom((q as any).templateActiveFrom ? (q as any).templateActiveFrom.slice(0, 10) : '');
          setTemplateActiveUntil((q as any).templateActiveUntil ? (q as any).templateActiveUntil.slice(0, 10) : '');
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Kunne ikke hente tilbud';
          setSnackbar({ open: true, message: msg, severity: 'error' });
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totalMoney = useMemo(() => {
    const addrSum = (quoteAddress.items || []).reduce((s, it) => s + (it.unitPrice || 0) * (it.quantity || 1), 0);
    const lineSum = [...pickupLines, ...dropoffLines].reduce((s, l) => s + lineTotal(l), 0);
    const extraSum = extraPriceLines.reduce((s, l) => s + l.amount * l.quantity, 0);
    return addrSum + lineSum + extraSum;
  }, [quoteAddress.items, pickupLines, dropoffLines, extraPriceLines]);

  const buildPayload = (): QuoteCreateInput => {
    const extras: QuotePriceLine[] = extraPriceLines
      .filter(pl => pl.description.trim() && (pl.amount !== 0 || pl.quantity !== 0))
      .map(pl => ({ description: pl.description, amount: pl.amount, quantity: pl.quantity, unitName: pl.unitName }));
    return {
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      customerPhone: customerPhone.trim() || null,
      customerNumber: customerNumber.trim() || null,
      quoteType,
      useNewAddressModel: true,
      quoteAddress,
      pickupLines,
      dropoffLines,
      pickups: [], dropoffs: [],
      priceLines: extras,
      totalAmount: totalMoney,
      validUntil: validUntil ? new Date(validUntil).toISOString() : null,
      terms: terms || null,
      customerMessage: customerMessage || null,
      internalNote: internalNote || null,
      quoteVisibility: visibility,
      templateActiveFrom: templateActiveFrom ? new Date(templateActiveFrom).toISOString() : null,
      templateActiveUntil: templateActiveUntil ? new Date(templateActiveUntil).toISOString() : null,
    } as QuoteCreateInput & { useNewAddressModel: boolean; quoteAddress: QuoteAddress; pickupLines: QuoteMovementLine[]; dropoffLines: QuoteMovementLine[]; quoteVisibility: QuoteVisibility; templateActiveFrom: string | null; templateActiveUntil: string | null };
  };

  const validate = (): string | null => {
    if (!customerName.trim()) return 'Kundenavn kræves';
    if (!customerEmail.trim()) return 'Email kræves';
    if (!quoteAddress.address && !quoteAddress.placeName) return 'Tilbudsadresse kræves (mindst navn eller adresse)';
    const hasMovements = pickupLines.length + dropoffLines.length > 0;
    const hasAddressItems = (quoteAddress.items || []).length > 0;
    if (!hasMovements && !hasAddressItems) return 'Mindst én vare på tilbudsadressen eller én bevægelseslinje kræves';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setSnackbar({ open: true, message: err, severity: 'error' }); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit && id) {
        await updateQuote.mutateAsync({ id, data: payload });
        navigate(`/tilbud/${id}`);
      } else {
        const result = await createQuote.mutateAsync(payload);
        navigate(`/tilbud/${result.id}`);
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (e instanceof Error ? e.message : 'Kunne ikke gemme tilbud');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isEdit ? `/tilbud/${id}` : '/tilbud')} size="small">
            Tilbage
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {isEdit && existingQuote ? `Rediger #${existingQuote.quoteNumber}` : 'Nyt tilbud'}
          </Typography>
        </Box>
        <TextField select size="small" label="Tilbudstype" value={quoteType}
          onChange={(e) => setQuoteType(e.target.value as QuoteType)} sx={{ minWidth: 180 }}>
          {QUOTE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
      </Box>

      <Grid container spacing={2}>
        {/* Kunde */}
        <Grid item xs={12}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kunde</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={5}>
                <Autocomplete freeSolo size="small" options={customers}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : (o.name || ''))}
                  isOptionEqualToValue={(a, b) => typeof a !== 'string' && typeof b !== 'string' && a.customerNumber === b.customerNumber}
                  inputValue={customerName}
                  onInputChange={(_, v) => setCustomerName(v)}
                  onChange={(_, v) => {
                    if (v && typeof v !== 'string') {
                      setCustomerName(v.name || '');
                      if (v.email) setCustomerEmail(v.email);
                      if (v.phone) setCustomerPhone(v.phone);
                      setCustomerNumber(v.customerNumber || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.customerNumber}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.customerNumber}{option.email ? ` · ${option.email}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Kundenavn *" />}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Kundenr." value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Telefon" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" label="Email *" type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={1}>
                <TextField fullWidth size="small" label="Gyldig" type="date" InputLabelProps={{ shrink: true }} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </Grid>
            </Grid>
          </CardContent></Card>
        </Grid>

        {/* Tilbudsadresse — Google først, derefter kompakte felter */}
        <Grid item xs={12}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Tilbudsadresse</Typography>
            <Box sx={{ mb: 1 }}>
              <PlacesAutocomplete
                label=""
                placeholder="🔍 Søg adresse på Google for at autoudfylde…"
                onChange={(p: PlaceResult | null) => {
                  if (!p) return;
                  setQuoteAddress(prev => ({
                    ...prev,
                    address: p.address || prev.address,
                    postcode: p.postcode || prev.postcode,
                    city: p.city || prev.city,
                    country: p.country || prev.country || 'DK',
                  }));
                }}
              />
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <Autocomplete freeSolo size="small" options={salesLocations}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                  inputValue={quoteAddress.placeName || ''}
                  onInputChange={(_, v) => setQuoteAddress(prev => ({ ...prev, placeName: v, salesLocationId: null }))}
                  onChange={(_, v) => {
                    if (v && typeof v !== 'string') {
                      setQuoteAddress(prev => ({
                        ...prev, salesLocationId: v.id, placeName: v.name,
                        address: v.address || '', postcode: v.postcode || '', city: v.city || '',
                        country: v.country || 'DK',
                      }));
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Lokationsnavn" placeholder="Fx byggeplads" />}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Adresse" value={quoteAddress.address || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, address: e.target.value }))} />
              </Grid>
              <Grid item xs={4} sm={1}>
                <TextField fullWidth size="small" label="Postnr" value={quoteAddress.postcode || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, postcode: e.target.value }))} />
              </Grid>
              <Grid item xs={8} sm={2}>
                <TextField fullWidth size="small" label="By" value={quoteAddress.city || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, city: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={1}>
                <TextField fullWidth size="small" label="Ref" value={quoteAddress.ref || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, ref: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Kontakt" value={quoteAddress.contact || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, contact: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Kontakt-tlf" value={quoteAddress.contactPhone || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, contactPhone: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={1}>
                <TextField fullWidth size="small" label="Land" value={quoteAddress.country || 'DK'} onChange={(e) => setQuoteAddress(prev => ({ ...prev, country: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Dato" type="date" InputLabelProps={{ shrink: true }}
                  value={quoteAddress.date || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, date: e.target.value }))} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Tid" type="time" InputLabelProps={{ shrink: true }}
                  value={quoteAddress.time || ''} onChange={(e) => setQuoteAddress(prev => ({ ...prev, time: e.target.value }))} />
              </Grid>
            </Grid>

            {/* Items på selve tilbudsadressen — fx kranarbejde der udføres på stedet */}
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Box>
                  <Typography variant="caption" fontWeight={600}>Varer på tilbudsadressen</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Til arbejde der udføres direkte på tilbudsadressen uden flytning af gods (fx kranarbejde).
                    Bliver til én DORA-cargo med pickup = dropoff = tilbudsadressen.
                  </Typography>
                </Box>
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setQuoteAddress(prev => ({ ...prev, items: [...(prev.items || []), emptyItem()] }))}>
                  Tilføj vare
                </Button>
              </Box>
              {(quoteAddress.items || []).length === 0 ? null : (
                <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.5 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 260 }}>Vare</TableCell>
                      <TableCell>Beskrivelse</TableCell>
                      <TableCell sx={{ width: 70 }}>Antal</TableCell>
                      <TableCell sx={{ width: 90 }}>Enhed</TableCell>
                      <TableCell sx={{ width: 100 }}>Pris</TableCell>
                      <TableCell sx={{ width: 110 }} align="right">Total</TableCell>
                      <TableCell sx={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(quoteAddress.items || []).map((item, itemIdx) => (
                      <QuoteAddressItemRow
                        key={item.id}
                        item={item} products={products} units={units}
                        onUpdate={(patch) => setQuoteAddress(prev => ({
                          ...prev,
                          items: (prev.items || []).map((it, i) => i === itemIdx ? { ...it, ...patch } : it),
                        }))}
                        onRemove={() => setQuoteAddress(prev => ({
                          ...prev,
                          items: (prev.items || []).filter((_, i) => i !== itemIdx),
                        }))}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </CardContent></Card>
        </Grid>

        {/* Leveringer TIL */}
        <Grid item xs={12}>
          <MovementSection
            title="Leveringer TIL tilbudsadressen"
            subtitle="Adresser vi henter materiale fra og leverer på tilbudsadressen"
            kind="pickup"
            lines={pickupLines} setLines={setPickupLines}
            salesLocations={salesLocations} products={products} units={units}
          />
        </Grid>

        {/* Bortkørsler FRA */}
        <Grid item xs={12}>
          <MovementSection
            title="Bortkørsler FRA tilbudsadressen"
            subtitle="Adresser vi henter materiale fra tilbudsadressen og leverer ud til"
            kind="dropoff"
            lines={dropoffLines} setLines={setDropoffLines}
            salesLocations={salesLocations} products={products} units={units}
          />
        </Grid>

        {/* Skabelon-perioder + synlighed */}
        <Grid item xs={12} md={6}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Skabelon-perioder (DORA)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Styrer hvornår DORA-cargo-skabelonen oprettes hhv. udløber. Tom = opret straks ved accept / aldrig udløb.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Opret-ved (start)" type="date" InputLabelProps={{ shrink: true }}
                  value={templateActiveFrom} onChange={(e) => setTemplateActiveFrom(e.target.value)}
                  helperText="Skabelon dukker først op i DORA på denne dato" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Slet-ved (udløb)" type="date" InputLabelProps={{ shrink: true }}
                  value={templateActiveUntil} onChange={(e) => setTemplateActiveUntil(e.target.value)}
                  helperText="Markeres udløbet — slettes manuelt i DORA (API kan ikke slette)" />
              </Grid>
            </Grid>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Synlighed på kunde-PDF</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Vælg hvilke felter kunden ser. Standard: kun tilbudsadresse, datoer og varelinjer er synlige.
            </Typography>
            <Grid container spacing={0}>
              {([
                ['showQuoteAddress', 'Tilbudsadresse'],
                ['showQuoteAddressContact', 'Kontakt på tilbudsadresse'],
                ['showQuoteAddressRef', 'Reference/sagsnr'],
                ['showMovementAddresses', 'Leverings-/bortkørselsadresser'],
                ['showDates', 'Datoer'],
                ['showItems', 'Varelinjer'],
                ['showItemPrices', 'Priser pr. varelinje'],
                ['showCargoType', 'Godstype'],
                ['showLineDescription', 'Godsbeskrivelse pr. linje'],
                ['showWeight', 'Vægt'],
                ['showLoadmeters', 'Loadmeter'],
                ['showLineRef', 'Ref på bevægelseslinjer'],
                ['showLineContact', 'Kontakt på bevægelseslinjer'],
                ['showCustomerNumber', 'Kundenummer'],
              ] as Array<[keyof QuoteVisibility, string]>).map(([key, label]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox size="small" checked={visibility[key]}
                        onChange={(e) => setVisibility(prev => ({ ...prev, [key]: e.target.checked }))} />
                    }
                    label={<Typography variant="caption">{label}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent></Card>
        </Grid>

        {/* Ekstra prislinjer */}
        <Grid item xs={12}>
          <Card><CardContent sx={{ pb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>Ekstra prislinjer</Typography>
                <Typography variant="caption" color="text.secondary">Linjer der ikke er knyttet til en specifik afhentning/bortkørsel (fx miljøtillæg, opstartsgebyr).</Typography>
              </Box>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setExtraPriceLines(prev => [...prev, emptyExtra()])}>
                Tilføj
              </Button>
            </Box>
            {extraPriceLines.length === 0 ? (
              <Typography variant="body2" color="text.disabled">Ingen ekstra linjer.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Beskrivelse</TableCell>
                    <TableCell sx={{ width: 90 }}>Antal</TableCell>
                    <TableCell sx={{ width: 90 }}>Enhed</TableCell>
                    <TableCell sx={{ width: 120 }}>Pris (kr)</TableCell>
                    <TableCell sx={{ width: 120 }} align="right">Total</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extraPriceLines.map((pl, idx) => (
                    <TableRow key={idx}>
                      <TableCell><TextField fullWidth size="small" sx={cellSx} value={pl.description} onChange={(e) => setExtraPriceLines(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} /></TableCell>
                      <TableCell><TextField fullWidth size="small" type="number" sx={cellSx} value={pl.quantity} onChange={(e) => setExtraPriceLines(prev => prev.map((x, i) => i === idx ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} /></TableCell>
                      <TableCell><TextField fullWidth size="small" sx={cellSx} value={pl.unitName} onChange={(e) => setExtraPriceLines(prev => prev.map((x, i) => i === idx ? { ...x, unitName: e.target.value } : x))} /></TableCell>
                      <TableCell><TextField fullWidth size="small" type="number" sx={cellSx} value={pl.amount} onChange={(e) => setExtraPriceLines(prev => prev.map((x, i) => i === idx ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} /></TableCell>
                      <TableCell align="right">{fmtMoney(pl.amount * pl.quantity)}</TableCell>
                      <TableCell><IconButton size="small" onClick={() => setExtraPriceLines(prev => prev.filter((_, i) => i !== idx))}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </Grid>

        {/* Besked + vilkår + intern note + total */}
        <Grid item xs={12} md={6}>
          <Card><CardContent>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Besked til kunde</Typography>
            <TextField fullWidth multiline rows={3} size="small" value={customerMessage} onChange={(e) => setCustomerMessage(e.target.value)} placeholder="Vises øverst i tilbuddet" />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>Vilkår</Typography>
            <TextField fullWidth multiline rows={3} size="small" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Betalingsbetingelser, ansvar, mv." />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardContent>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Intern note</Typography>
            <TextField fullWidth multiline rows={3} size="small" value={internalNote} onChange={(e) => setInternalNote(e.target.value)} placeholder="Vises ikke til kunden" />
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">Total (uden moms):</Typography>
              <Typography variant="h6" fontWeight={700}>{fmtMoney(totalMoney)} kr.</Typography>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, zIndex: 1100 }}>
        <Button onClick={() => navigate(isEdit ? `/tilbud/${id}` : '/tilbud')}>Annuller</Button>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving}>
          {isEdit ? 'Gem ændringer' : 'Opret tilbud'}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

// =================================================================
// MovementSection — kompakt tabel pr. retning, med varer som ekstra-rækker
// =================================================================
interface MovementSectionProps {
  title: string;
  subtitle: string;
  kind: 'pickup' | 'dropoff';
  lines: QuoteMovementLine[];
  setLines: React.Dispatch<React.SetStateAction<QuoteMovementLine[]>>;
  salesLocations: SalesLocation[];
  products: DoraProduct[];
  units: DoraFinanceUnit[];
}

function MovementSection({ title, subtitle, lines, setLines, salesLocations, products, units }: MovementSectionProps) {
  // Map unit-id → name for hurtig oversættelse af Product.unitId
  const unitNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units) m.set(u.id, u.name);
    return m;
  }, [units]);
  // Sortér produkter numerisk på productNumber (så 3, 4, 345 ikke kommer som 3, 345, 4)
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const aNum = parseInt(a.productNumber, 10);
      const bNum = parseInt(b.productNumber, 10);
      if (!isNaN(aNum) && !isNaN(bNum) && aNum !== bNum) return aNum - bNum;
      return (a.productNumber || '').localeCompare(b.productNumber || '', 'da');
    });
  }, [products]);

  const updateLine = (idx: number, patch: Partial<QuoteMovementLine>) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };
  const removeLine = (idx: number) => setLines(prev => prev.filter((_, i) => i !== idx));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);

  const updateItem = (lineIdx: number, itemIdx: number, patch: Partial<QuoteMovementItem>) => {
    setLines(prev => prev.map((l, i) => i === lineIdx
      ? { ...l, items: (l.items || []).map((it, j) => j === itemIdx ? { ...it, ...patch } : it) }
      : l));
  };
  const addItem = (lineIdx: number) => {
    setLines(prev => prev.map((l, i) => i === lineIdx
      ? { ...l, items: [...(l.items || []), emptyItem()] }
      : l));
  };
  const removeItem = (lineIdx: number, itemIdx: number) => {
    setLines(prev => prev.map((l, i) => i === lineIdx
      ? { ...l, items: (l.items || []).filter((_, j) => j !== itemIdx) }
      : l));
  };

  return (
    <Card><CardContent sx={{ pb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={addLine} variant="outlined">
          Tilføj adresse
        </Button>
      </Box>

      {lines.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
          Ingen linjer. Tilføj én ovenfor.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36 }}>#</TableCell>
              <TableCell sx={{ width: 220 }}>Søg adresse på Google</TableCell>
              <TableCell sx={{ width: 160 }}>Lokationsnavn</TableCell>
              <TableCell sx={{ width: 200 }}>Adresse</TableCell>
              <TableCell sx={{ width: 80 }}>Postnr</TableCell>
              <TableCell sx={{ width: 130 }}>By</TableCell>
              <TableCell sx={{ width: 130 }}>Dato</TableCell>
              <TableCell sx={{ width: 80 }}>Tid</TableCell>
              <TableCell align="right" sx={{ width: 100 }}>Linje-total</TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, lineIdx) => {
              const total = lineTotal(line);
              return (
                <Fragment key={line.id || lineIdx}>
                  {/* HOVED-RÆKKE (adresse) */}
                  <TableRow hover sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>{lineIdx + 1}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <PlacesAutocomplete
                        label=""
                        placeholder="🔍 Søg adresse…"
                        onChange={(p: PlaceResult | null) => {
                          if (!p) return;
                          updateLine(lineIdx, {
                            address: p.address || line.address,
                            postcode: p.postcode || line.postcode,
                            city: p.city || line.city,
                            country: p.country || line.country || 'DK',
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete freeSolo size="small" options={salesLocations}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                        inputValue={line.placeName || ''}
                        onInputChange={(_, v) => updateLine(lineIdx, { placeName: v, salesLocationId: null })}
                        onChange={(_, v) => {
                          if (v && typeof v !== 'string') updateLine(lineIdx, {
                            salesLocationId: v.id, placeName: v.name,
                            address: v.address || '', postcode: v.postcode || '', city: v.city || '',
                            country: v.country || 'DK',
                          });
                        }}
                        renderInput={(params) => <TextField {...params} sx={cellSx} />}
                      />
                    </TableCell>
                    <TableCell><TextField fullWidth size="small" sx={cellSx} value={line.address || ''} onChange={(e) => updateLine(lineIdx, { address: e.target.value })} /></TableCell>
                    <TableCell><TextField fullWidth size="small" sx={cellSx} value={line.postcode || ''} onChange={(e) => updateLine(lineIdx, { postcode: e.target.value })} /></TableCell>
                    <TableCell><TextField fullWidth size="small" sx={cellSx} value={line.city || ''} onChange={(e) => updateLine(lineIdx, { city: e.target.value })} /></TableCell>
                    <TableCell><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} sx={cellSx} value={line.date || ''} onChange={(e) => updateLine(lineIdx, { date: e.target.value })} /></TableCell>
                    <TableCell><TextField fullWidth size="small" type="time" InputLabelProps={{ shrink: true }} sx={cellSx} value={line.time || ''} onChange={(e) => updateLine(lineIdx, { time: e.target.value })} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(total)}</TableCell>
                    <TableCell>
                      <Tooltip title="Slet adresse">
                        <IconButton size="small" onClick={() => removeLine(lineIdx)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* SUB-RÆKKE: gods-detaljer */}
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={9}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField select size="small" label="Godstype" sx={{ minWidth: 140 }} value={line.cargoType || ''} onChange={(e) => updateLine(lineIdx, { cargoType: e.target.value })}>
                          <MenuItem value="">—</MenuItem>
                          {CARGO_TYPES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Beskrivelse" sx={{ minWidth: 260, flexGrow: 1 }} value={line.description || ''} onChange={(e) => updateLine(lineIdx, { description: e.target.value })} />
                        <TextField size="small" label="Vægt (kg)" sx={{ width: 110 }} value={line.weight || ''} onChange={(e) => updateLine(lineIdx, { weight: e.target.value })} />
                        <TextField size="small" label="Loadmeter" sx={{ width: 110 }} value={line.loadmeters || ''} onChange={(e) => updateLine(lineIdx, { loadmeters: e.target.value })} />
                        <TextField size="small" label="Ref" sx={{ width: 110 }} value={line.ref || ''} onChange={(e) => updateLine(lineIdx, { ref: e.target.value })} />
                        <TextField size="small" label="Kontakt" sx={{ width: 140 }} value={line.contact || ''} onChange={(e) => updateLine(lineIdx, { contact: e.target.value })} />
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* VARER-RÆKKER */}
                  {(line.items || []).map((item, itemIdx) => {
                    const selectedProduct = sortedProducts.find(p => p.id === item.productId) || null;
                    return (
                    <Fragment key={`i-${line.id}-${item.id}`}>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell />
                      <TableCell sx={{ pl: 3 }}>
                        <Autocomplete<DoraProduct, false, false, false>
                          size="small" options={sortedProducts}
                          getOptionLabel={(o) => o ? `${o.productNumber} · ${o.name}` : ''}
                          isOptionEqualToValue={(o, v) => !!o && !!v && o.id === v.id}
                          value={selectedProduct}
                          onChange={(_, v) => {
                            if (!v) {
                              // Clear-knap (X) — nulstil produkt-felterne men bevar antal/enhed
                              updateItem(lineIdx, itemIdx, {
                                productId: null, productNumber: '', productName: '',
                                description: '', unitPrice: 0,
                              });
                              return;
                            }
                            const price = v.salesPrice ? parseFloat(v.salesPrice) : 0;
                            const resolvedUnit = v.unitId ? unitNameById.get(v.unitId) : undefined;
                            updateItem(lineIdx, itemIdx, {
                              productId: v.id, productNumber: v.productNumber,
                              productName: v.name, description: v.name,
                              unitPrice: price > 0 ? price : item.unitPrice,
                              // Brug DORA's finance-unit som enhed — men kun hvis brugeren ikke selv har
                              // valgt en specifik enhed (vi behandler 'stk'-default som "ikke valgt").
                              ...(resolvedUnit && (!item.unitName || item.unitName === 'stk') ? { unitName: resolvedUnit } : {}),
                            });
                          }}
                          renderInput={(params) => <TextField {...params} placeholder="Vælg vare…" sx={cellSx} />}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {option.productNumber} · {option.name}
                                </Typography>
                                {option.salesPrice && (
                                  <Typography variant="caption" color="text.secondary">
                                    {fmtMoney(parseFloat(option.salesPrice))} kr.
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                        />
                      </TableCell>
                      <TableCell colSpan={3}>
                        <TextField fullWidth size="small" placeholder="Beskrivelse (vises på faktura)" sx={cellSx}
                          value={item.description || ''} onChange={(e) => updateItem(lineIdx, itemIdx, { description: e.target.value })} />
                      </TableCell>
                      <TableCell><TextField fullWidth size="small" type="number" placeholder="Antal" sx={cellSx} value={item.quantity} onChange={(e) => updateItem(lineIdx, itemIdx, { quantity: parseFloat(e.target.value) || 0 })} /></TableCell>
                      <TableCell>
                        <Autocomplete
                          freeSolo size="small" options={units.length ? Array.from(new Set([...units.map(u => u.name), ...STANDARD_UNITS])) : STANDARD_UNITS}
                          value={item.unitName || ''}
                          onChange={(_, v) => updateItem(lineIdx, itemIdx, { unitName: typeof v === 'string' ? v : '' })}
                          onInputChange={(_, v) => updateItem(lineIdx, itemIdx, { unitName: v })}
                          renderInput={(params) => <TextField {...params} placeholder="Enhed" sx={cellSx} />}
                        />
                      </TableCell>
                      <TableCell><TextField fullWidth size="small" type="number" placeholder="Pris" sx={cellSx} value={item.unitPrice} onChange={(e) => updateItem(lineIdx, itemIdx, { unitPrice: parseFloat(e.target.value) || 0 })} /></TableCell>
                      <TableCell align="right">{fmtMoney((item.unitPrice || 0) * (item.quantity || 1))}</TableCell>
                      <TableCell>
                        <Tooltip title="Slet vare">
                          <IconButton size="small" onClick={() => removeItem(lineIdx, itemIdx)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell />
                      <TableCell colSpan={8} sx={{ pt: 0, pb: 0.75 }}>
                        <TextField fullWidth size="small" variant="standard" sx={cellSx}
                          placeholder="Intern note (vises ikke til kunden — sendes til DORA cargo-note ved accept/konvertering)"
                          value={item.internalNote || ''}
                          onChange={(e) => updateItem(lineIdx, itemIdx, { internalNote: e.target.value })} />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    </Fragment>
                    );
                  })}

                  {/* TILFØJ-VARE RÆKKE */}
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={9} sx={{ borderBottom: 2, borderColor: 'divider', pb: 1 }}>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addItem(lineIdx)} variant="text">
                        Tilføj vare til denne adresse
                      </Button>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </CardContent></Card>
  );
}

// =================================================================
// QuoteAddressItemRow — én række til en vare direkte på tilbudsadressen
// =================================================================
interface QuoteAddressItemRowProps {
  item: QuoteMovementItem;
  products: DoraProduct[];
  units: DoraFinanceUnit[];
  onUpdate: (patch: Partial<QuoteMovementItem>) => void;
  onRemove: () => void;
}

function QuoteAddressItemRow({ item, products, units, onUpdate, onRemove }: QuoteAddressItemRowProps) {
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const aNum = parseInt(a.productNumber, 10);
      const bNum = parseInt(b.productNumber, 10);
      if (!isNaN(aNum) && !isNaN(bNum) && aNum !== bNum) return aNum - bNum;
      return (a.productNumber || '').localeCompare(b.productNumber || '', 'da');
    });
  }, [products]);
  const unitNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units) m.set(u.id, u.name);
    return m;
  }, [units]);
  const selectedProduct = sortedProducts.find(p => p.id === item.productId) || null;
  return (
    <Fragment>
    <TableRow>
      <TableCell>
        <Autocomplete<DoraProduct, false, false, false>
          size="small" options={sortedProducts}
          getOptionLabel={(o) => o ? `${o.productNumber} · ${o.name}` : ''}
          isOptionEqualToValue={(o, v) => !!o && !!v && o.id === v.id}
          value={selectedProduct}
          onChange={(_, v) => {
            if (!v) {
              onUpdate({ productId: null, productNumber: '', productName: '', description: '', unitPrice: 0 });
              return;
            }
            const price = v.salesPrice ? parseFloat(v.salesPrice) : 0;
            const resolvedUnit = v.unitId ? unitNameById.get(v.unitId) : undefined;
            onUpdate({
              productId: v.id, productNumber: v.productNumber,
              productName: v.name, description: v.name,
              unitPrice: price > 0 ? price : item.unitPrice,
              ...(resolvedUnit && (!item.unitName || item.unitName === 'stk') ? { unitName: resolvedUnit } : {}),
            });
          }}
          renderInput={(params) => <TextField {...params} placeholder="Vælg vare…" sx={cellSx} />}
        />
      </TableCell>
      <TableCell>
        <TextField fullWidth size="small" placeholder="Beskrivelse" sx={cellSx}
          value={item.description || ''} onChange={(e) => onUpdate({ description: e.target.value })} />
      </TableCell>
      <TableCell>
        <TextField fullWidth size="small" type="number" placeholder="Antal" sx={cellSx}
          value={item.quantity} onChange={(e) => onUpdate({ quantity: parseFloat(e.target.value) || 0 })} />
      </TableCell>
      <TableCell>
        <Autocomplete freeSolo size="small" options={STANDARD_UNITS}
          value={item.unitName || ''}
          onChange={(_, v) => onUpdate({ unitName: typeof v === 'string' ? v : '' })}
          onInputChange={(_, v) => onUpdate({ unitName: v })}
          renderInput={(params) => <TextField {...params} placeholder="Enhed" sx={cellSx} />}
        />
      </TableCell>
      <TableCell>
        <TextField fullWidth size="small" type="number" placeholder="Pris" sx={cellSx}
          value={item.unitPrice} onChange={(e) => onUpdate({ unitPrice: parseFloat(e.target.value) || 0 })} />
      </TableCell>
      <TableCell align="right">{fmtMoney((item.unitPrice || 0) * (item.quantity || 1))}</TableCell>
      <TableCell>
        <Tooltip title="Slet vare">
          <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={6} sx={{ pt: 0, pb: 0.75 }}>
        <TextField fullWidth size="small" variant="standard" sx={cellSx}
          placeholder="Intern note (vises ikke til kunden — sendes til DORA cargo-note ved accept/konvertering)"
          value={item.internalNote || ''}
          onChange={(e) => onUpdate({ internalNote: e.target.value })} />
      </TableCell>
      <TableCell />
    </TableRow>
    </Fragment>
  );
}
